import {FC, useMemo, useState} from "react";
import {App, Button, Card, DatePicker, Flex, Form, FormProps, Input, Segmented, Select, Typography} from "antd";
import styles from './CreatePage.module.css'
import {TitleFormItem} from "../../components/TitleFormItem/TitleFormItem.tsx";
import {Airport, AirportWithTimePost, EventPost, EventPostTypeAirports} from "../../types/domain.ts";
import {AirportWithTimeSelector} from "./components/AirportWithTimeSelector/AirportWithTimeSelector.tsx";
import {EVENT_TYPE_OPTIONS, MOSCOW_AIRPORTS} from "../../constants/constants.ts";
import {useCategories} from "../../queries/useCategories.ts";
import {ApprovalsSelector} from "./components/ApprovalsSelector/ApprovalsSelector.tsx";
import {useAirports} from "../../queries/useAirports.ts";
import {BannerUpload} from "../../components/BannerUpload/BannerUpload.tsx";
import {SwapOutlined} from "@ant-design/icons";
import {Blocker} from "./components/Blocker/Blocker.tsx";
import {uploadImage} from "../../modules/uploadImage.ts";
import {eventsPost} from "../../api/events/eventsPost.ts";
import dayjs from "dayjs";
import {useNavigate} from "react-router";
import {BannerRulesModal} from "../../components/BannerRulesModal/BannerRulesModal.tsx";
import {useIsMobile} from "../../hooks/useIsMobile.ts";
import {useUserRegion} from "./hooks/useUserRegion.ts";

type CreateFormType = EventPost & {
    selectedRegion?: number;
    image?: { originFileObj: File }[];
}

export const CreatePage: FC = () => {
    const { modal, message } = App.useApp();
    const isMobile = useIsMobile();

    const [form] = Form.useForm<CreateFormType>();
    const eventType = Form.useWatch('type', form);

    const { data: categories } = useCategories();
    const categoriesOptions = useMemo(() => categories?.map(({ id, name }) => ({
        value: id,
        label: name
    })), [categories]);

    const arrivals = Form.useWatch('arrivals', form);
    const departures = Form.useWatch('departures', form);
    const airports = Form.useWatch('airports', form);

    const syncAirportsTimes = (from?: string, to?: string) => {
        if(!from && !to) return;

        const freshAirports = form.getFieldValue('airports');
        form.setFieldValue('airports', freshAirports.map((a: EventPostTypeAirports['airports'][number]) => ({
            ...a,
            ...(from ? { from } : {}),
            ...(to ? { to } : {})
        })));
    }

    const { data: airportsList } = useAirports(eventType === 'route');
    const [titleChangedManually, setTitleChangedManually] = useState(false);
    const [timeEnforced, setTimeEnforced] = useState(false);
    const handleValuesChange: FormProps<CreateFormType>['onValuesChange'] = (values) => {
        if(values.type) {
            form.setFieldValue('category_id', undefined);

            if(values.type === 'airports') {
                form.setFieldValue('airports', form.getFieldValue('departures'));
            } else {
                form.setFieldValue('departures', form.getFieldValue('airports'));
            }
        }

        if(values.airports && timeEnforced) {
            const changedPort = values.airports.filter((n: unknown) => n)[0];
            const changedFrom = changedPort?.from;
            const changedTo = changedPort?.to;
            if(changedFrom || changedTo) {
                syncAirportsTimes(changedFrom, changedTo);
            }
        }

        if(values.type || values.category_id || values.airports || values.arrivals || values.departures) {
            if(titleChangedManually) return;
            const type = values.type || form.getFieldValue('type');
            const category = categories?.find(({ id }) => id === values.category_id || form.getFieldValue('category_id'));
            if(!airportsList) return;

            if(type === 'airports') {
                if(!category) return;

                const freshAirports = form.getFieldValue('airports') as AirportWithTimePost[];
                const airportListNames = freshAirports.map(({ airport_id }) => airportsList.find(({ id }) => id === airport_id)?.name).filter(v => v);
                if(!airportListNames.length) return;

                form.setFieldValue('name', `${category.name}: ${airportListNames.join(', ')}`);
            }
            if(type === 'route') {
                const freshDepartures = form.getFieldValue('departures') as AirportWithTimePost[];
                const freshArrivals = form.getFieldValue('arrivals') as AirportWithTimePost[];

                const departuresObjs = freshDepartures.map(({ airport_id }) => airportsList.find(({ id }) => id === airport_id)).filter(v => v) as Airport[];
                const arrivalsObjs = freshArrivals.map(({ airport_id }) => airportsList.find(({ id }) => id === airport_id)).filter(v => v) as Airport[];

                if(!departuresObjs.length && !arrivalsObjs.length) return;

                const joinedDepartures = (MOSCOW_AIRPORTS.every(v => departuresObjs.find(({ id }) => id === v)) ? // includes all moscow airports
                    [`Москва (ШРМ, ВКО, ДМЕ)`, ...departuresObjs.filter(({ id }) => !MOSCOW_AIRPORTS.includes(id)).map(({ name }) => name)] :
                    departuresObjs.map(({ name }) => name)
                ).join(' + ');
                const joinedArrivals = (MOSCOW_AIRPORTS.every(v => arrivalsObjs.find(({ id }) => id === v)) ? // includes all moscow airports
                        [`Москва (ШРМ, ВКО, ДМЕ)`, ...arrivalsObjs.filter(({ id }) => !MOSCOW_AIRPORTS.includes(id)).map(({ name }) => name)] :
                        arrivalsObjs.map(({ name }) => name)
                ).join(' + ');

                form.setFieldValue('name', `${joinedDepartures || '...'} — ${joinedArrivals || '...'}`);
            }
        }
    }

    const handleSwap = (i: number) => {
        const temp = form.getFieldValue(['departures', i]);
        form.setFieldValue(['departures', i], form.getFieldValue(['arrivals', i]));
        form.setFieldValue(['arrivals', i], temp);
    }

    const navigate = useNavigate();
    const [isDraft, setIsDraft] = useState(false);
    const [isDraftLoading, setIsDraftLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const handleFinish: FormProps<CreateFormType>['onFinish'] = async ({ image, date, ...rest }) => {
        if(isDraft) setIsDraftLoading(true);
        else setIsLoading(true);

        let image_id: string | undefined;
        try {
            image_id = await uploadImage(image);
        } catch(e) {
            void message.error(`Произошла ошибка при загрузке баннера`);
            console.error(e);
            setIsDraftLoading(false);
            setIsLoading(false);
        }

        return eventsPost({
            ...rest,
            date: dayjs(date).utc(true).format('YYYY-MM-DD'),
            image_id
        }, isDraft).then(({ payload }) => {
            void message.success(isDraft ? 'Черновик создан' : 'Ивент создан');
            navigate(`/events/${payload.id}`);
        }).catch((e) => {
            void message.error(`Ошибка (${JSON.stringify(e)})`);
            console.error(e);
        }).finally(() => {
            setIsDraftLoading(false);
            setIsLoading(false);
        })
    }

    const [defaultRegion, regionsToSelect] = useUserRegion();
    const selectedRegion = Form.useWatch('selectedRegion', form);

    return (
        <Form<CreateFormType>
            form={form}
            className={styles.container}
            layout="vertical"
            initialValues={{
                name: 'Новый ивент',
                name_en: 'New event',
                type: 'airports',
                arrivals: [{ airport_id: undefined, from: undefined, to: undefined }],
                departures: [{ airport_id: undefined, from: undefined, to: undefined  }],
                airports: [{ airport_id: undefined, from: undefined, to: undefined  }],
                custom_approvers_ids: []
            }}
            onValuesChange={handleValuesChange}
            onFinish={handleFinish}
        >
            <Card
                title={(
                    <Flex align={!isMobile ? 'center' : undefined} justify="space-between" vertical={isMobile} gap={isMobile ? 16 : undefined}>
                        <Flex align="center" gap={8}>
                            <Form.Item<CreateFormType> noStyle name="name">
                                <TitleFormItem
                                    level={3}
                                    onTouched={() => setTitleChangedManually(true)}
                                />
                            </Form.Item>
                            <Typography.Title level={3} type="secondary" style={{ margin: 0 }}>/</Typography.Title>
                            <Form.Item<CreateFormType> noStyle name="name_en">
                                <TitleFormItem level={3} type="secondary" />
                            </Form.Item>
                        </Flex>
                        <Flex align={!isMobile ? 'center' : undefined} gap={16} vertical={isMobile}>
                            <Form.Item<CreateFormType> noStyle name="type">
                                <Segmented
                                    options={EVENT_TYPE_OPTIONS}
                                />
                            </Form.Item>
                            <Form.Item<CreateFormType>
                                noStyle
                                name="category_id"
                                rules={[{ type: 'string', required: eventType === 'airports' }]}
                            >
                                <Select
                                    placeholder="Тип ивента"
                                    disabled={eventType === 'route'}
                                    options={categoriesOptions}
                                />
                            </Form.Item>
                        </Flex>
                    </Flex>
                )}
                className={styles.card}
                classNames={{ body: styles.cardBody }}
            >
                <Flex gap={16} vertical={isMobile}>
                    <Flex vertical flex="1 0" className={styles.halfCard}>
                        {regionsToSelect && (
                            <Form.Item<CreateFormType>
                                name="selectedRegion"
                                label="Регион приписки"
                                rules={[{ type: "number", required: true }]}
                            >
                                <Select
                                    options={regionsToSelect.map(({ id, name }) => ({
                                        label: name,
                                        value: id
                                    }))}
                                />
                            </Form.Item>
                        )}
                        <Form.Item<CreateFormType>
                            name="date"
                            label="Дата"
                            rules={[{ type: 'date', required: true }]}
                            extra="Предварительная дата, которая будет утверждена на финальном этапе согласования"
                        >
                            <DatePicker
                                format={{
                                    format: 'DD.MM.YYYY',
                                    type: 'mask'
                                }}
                                minDate={dayjs().add(1, 'day')}
                            />
                        </Form.Item>
                        <Form.Item<CreateFormType> name="description" label="Описание 🇷🇺">
                            <Input.TextArea
                                rows={3}
                            />
                        </Form.Item>
                        <Form.Item<CreateFormType> name="description_en" label="Описание 🇬🇧">
                            <Input.TextArea
                                rows={3}
                            />
                        </Form.Item>
                        {eventType === 'route' && (
                            <Form.Item<CreateFormType>
                                name="route"
                                label="Маршрут"
                                rules={[{ type: 'string', required: true, whitespace: true }]}
                            >
                                <Input.TextArea rows={2} />
                            </Form.Item>
                        )}
                        <Form.Item<CreateFormType>
                            label="Лист согласования"
                            name="custom_approvers_ids"
                        >
                            <ApprovalsSelector
                                selectedAirports={eventType === 'route' ? [...(arrivals || []), ...(departures || [])] : airports || []}
                                regionId={defaultRegion || selectedRegion || -1}
                            />
                        </Form.Item>
                    </Flex>
                    <Flex vertical flex="1 0" className={styles.halfCard}>
                        <Form.Item label="Баннер">
                            <Flex align="flex-start" vertical gap={6}>
                                <BannerUpload name="image" />
                                <BannerRulesModal />
                            </Flex>
                        </Form.Item>
                        {eventType === 'route' ? (
                            <Flex gap={8}>
                                <AirportWithTimeSelector
                                    airports={airportsList}
                                    name="departures"
                                    label="Вылеты"
                                />
                                <Flex vertical gap={32} className={styles.swapColumnContainer}>
                                    {Array(Math.min(departures?.length || 0, arrivals?.length || 0)).fill(0).map((_, i) => (
                                        <Button
                                            key={i}
                                            size="small"
                                            type="text"
                                            icon={<SwapOutlined />}
                                            onClick={() => handleSwap(i)}
                                        />
                                    ))}
                                </Flex>
                                <AirportWithTimeSelector
                                    airports={airportsList}
                                    name="arrivals"
                                    label="Прибытия"
                                    reversed
                                />
                            </Flex>
                        ) : (
                            <Flex>
                                <AirportWithTimeSelector
                                    airports={airportsList}
                                    name="airports"
                                    label="Аэропорты"
                                    timeEnforcer
                                    timeEnforced={timeEnforced}
                                    onTimeEnforcementChange={(v) => {
                                        setTimeEnforced(v);
                                        if(v) {
                                            const firstTime = form.getFieldValue(['airports', 0]);
                                            syncAirportsTimes(firstTime.from, firstTime.to);
                                        }
                                    }}
                                    restrictRepeats
                                />
                            </Flex>
                        )}
                    </Flex>
                </Flex>
                <Flex justify="flex-end" gap={16} vertical={isMobile}>
                    <Button
                        danger
                        onClick={async () => {
                            if(await modal.confirm({ title: 'Вы уверены?', content: 'Все данные будут удалены' })) {
                                form.resetFields();
                            }
                        }}
                        disabled={isDraftLoading || isLoading}
                    >
                        Очистить
                    </Button>
                    <Button
                        onClick={() => {
                            setIsDraft(true);
                            form.submit();
                        }}
                        loading={isDraftLoading}
                        disabled={isLoading}
                    >
                        Сохранить черновик
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={isLoading}
                        disabled={isDraftLoading}
                    >
                        Отправить на согласование
                    </Button>
                </Flex>
            </Card>
            <Blocker shouldBlock={() => form.isFieldsTouched() && !isLoading && !isDraftLoading} />
        </Form>
    );
}